import { useSession } from "next-auth/react";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { Button } from "~/components/v3/themed/Button";
import Modal from "~/components/v3/themed/Modal";
import {
    createMembershipOrOneOffByPassCheckoutSession,
    createOrValidateGuestUser,
} from "../actions";
import { Label, SubLabel } from "~/components/v3/shadcn/Label";
import LoginModal from "~/components/v3/themed/LoginModal";
import { Input } from "~/components/v3/themed/Input";
import { toast } from "react-toastify";

type Props = {
    tierId?: string;
    communityId?: string;
    eventSlug: string;
    isMember: boolean;
};

const PurchaseMembershipOrOneOffByPass = ({
    tierId,
    communityId,
    eventSlug,
    isMember,
}: Props) => {
    if (!tierId || !communityId) {
        return null;
    }
    const { data: session } = useSession();

    const queryParams = useSearchParams();

    const [purchaseMode, setPurchaseMode] = useState<
        "pick-option" | "guest" | "user" | "login"
    >("pick-option");
    const [isLoading, setIsLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [guestEmail, setGuestEmail] = useState("");
    const [error, setError] = useState<string | null>(null);
    const router = useRouter();

    useEffect(() => {
        if (!session?.user) {
            setPurchaseMode("pick-option");
        } else {
            if (queryParams) {
                const continueFromLogin =
                    queryParams.get("purchaseMode") === "user";
                if (continueFromLogin) {
                    setPurchaseMode("user");
                    setIsModalOpen(true);
                }
                return;
            }
            setPurchaseMode("user");
        }
    }, [session?.user, queryParams]);

    const handlePurchase = async (
        selectedMode: "membership" | "one-off-by-pass",
    ) => {
        setIsLoading(true);
        setError(null);
        try {
            let userId = session?.user?.id;

            if (purchaseMode === "guest") {
                const result = await createOrValidateGuestUser(guestEmail);

                userId = result.userId;
            }

            if (!userId) {
                toast.error("Something went wrong. Please try again.");
                throw new Error("No user ID found");
            }

            const { sessionUrl } =
                await createMembershipOrOneOffByPassCheckoutSession(
                    eventSlug,
                    tierId,
                    communityId,
                    userId,
                    selectedMode,
                );
            if (sessionUrl) {
                router.push(sessionUrl);
            } else {
                throw new Error("Failed to create checkout session");
            }
        } catch (error) {
            console.error("Error creating checkout session:", error);
            if (error instanceof Error) {
                if (error.message.includes("No ticket package associated")) {
                    toast.error(
                        "One-off tickets are not available for this event. Please choose the membership option.",
                    );
                } else if (
                    error.message.includes("No suitable ticket tier found")
                ) {
                    toast.error(
                        "There was an issue with the ticket configuration. Please contact support.",
                    );
                } else {
                    toast.error(
                        "An error occurred while setting up the purchase. Please try again.",
                    );
                }
            } else {
                toast.error("An unexpected error occurred. Please try again.");
            }
        } finally {
            setIsLoading(false);
            setIsModalOpen(false);
        }
    };

    if (isMember) {
        return (
            <Button variant="info" className="w-full">
                <FaCheckCircle />
                Already a member
            </Button>
        );
    }

    const renderModalContent = useCallback(() => {
        if (purchaseMode === "pick-option") {
            return (
                <div className="flex flex-col space-y-4">
                    <p>Choose how you'd like to proceed:</p>
                    <Button
                        onClick={() => {
                            setPurchaseMode("login");
                        }}
                        className="w-full"
                        variant="secondary"
                    >
                        Sign up / Log in to purchase
                    </Button>
                    <Button
                        onClick={() => {
                            setPurchaseMode("guest");
                        }}
                        className="w-full"
                        variant="tertiary"
                    >
                        Continue as guest
                    </Button>
                </div>
            );
        } else if (purchaseMode === "guest") {
            return (
                <div className="flex flex-col space-y-4">
                    <Label>Enter your email address</Label>
                    <Input
                        type="email"
                        value={guestEmail}
                        onChange={(e) => setGuestEmail(e.target.value)}
                        placeholder="your@email.com"
                    />
                    {error && <p className="text-red-500">{error}</p>}
                    <Button
                        onClick={() => handlePurchase("one-off-by-pass")}
                        className="w-full"
                        disabled={isLoading || !guestEmail}
                    >
                        Continue to purchase
                    </Button>
                </div>
            );
        } else {
            return (
                <div className="flex flex-col space-y-4">
                    <p>Please select your preferred purchase option:</p>
                    <Label>Monthly Subscription</Label>
                    <SubLabel>
                        Includes access to all future events for this chapter
                    </SubLabel>
                    <Button
                        onClick={() => handlePurchase("membership")}
                        className="w-full"
                        variant="secondary"
                        disabled={isLoading}
                    >
                        Purchase Membership
                    </Button>

                    <Label>One-Off Ticket</Label>
                    <SubLabel>Purchase a ticket for this event</SubLabel>
                    <Button
                        onClick={() => handlePurchase("one-off-by-pass")}
                        className="w-full"
                        variant="tertiary"
                        disabled={isLoading}
                    >
                        Purchase One-Off Ticket
                    </Button>
                </div>
            );
        }
    }, [purchaseMode]);

    if (purchaseMode === "login") {
        return (
            <LoginModal
                isOpen={true}
                setIsOpen={(open) => {
                    setIsModalOpen(open);
                    if (!open) {
                        setPurchaseMode("pick-option");
                    }
                }}
                title="Log in to Purchase Membership"
                description="You need to be logged in to purchase memberships"
                callbackUrl={`/events/${eventSlug}?purchaseMode=user`}
            />
        );
    }

    return (
        <>
            <Button
                onClick={() => setIsModalOpen(true)}
                disabled={isLoading}
                className="w-full"
            >
                {isLoading ? "Processing..." : "Purchase Options"}
            </Button>

            <Modal
                title="Purchase Options"
                open={isModalOpen}
                onOpenChange={(open) => {
                    setIsModalOpen(open);
                    if (!open) {
                        setPurchaseMode(session?.user ? "user" : "pick-option");
                        setError(null);
                    }
                }}
            >
                {renderModalContent()}
            </Modal>
        </>
    );
};

export default PurchaseMembershipOrOneOffByPass;
